import { EnvelopeIcon } from "@heroicons/react/24/outline";
import ms from "ms";
import { useAuth } from "@/auth";
import { AlertBar } from "@/components/AlertBar";
import { Button } from "@/components/catalyst/Button";
import { DateFnsFormat } from "@/components/DateFnsFormat";
import { useRefreshableData } from "@/hooks/use-refreshable-data";
import { useSendVerificationEmail } from "@/hooks/use-send-verification-email";

/**
 * Banner displayed when a user's email is not verified.
 * Prompts the user to verify their email.
 */
export function EmailVerificationBanner() {
	const { user } = useAuth();
	const sendVerificationEmail = useSendVerificationEmail();

	const { canRefresh, isRefreshing, cooldownEndsAt, refresh } =
		useRefreshableData({
			cooldownMs: ms("5m"),
			storageKey: "email_verification_last_sent",
			queryKey: "email-verification",
			onCacheBust: () => sendVerificationEmail(),
		});

	if (user.emailVerified) {
		return null;
	}

	const isDisabled = !canRefresh || isRefreshing;

	return (
		<AlertBar
			icon={EnvelopeIcon}
			variant="default"
			title="Email verification pending"
			action={
				<Button type="button" outline disabled={isDisabled} onClick={refresh}>
					{isRefreshing ? (
						"Sending..."
					) : !canRefresh && cooldownEndsAt ? (
						<>
							Resend
							<DateFnsFormat date={cooldownEndsAt} relative />
						</>
					) : (
						"Resend Email"
					)}
				</Button>
			}
		>
			We’ve sent a verification link to{" "}
			<span className="font-semibold">{user.email}</span>. You can continue
			setting up your app in the meantime.
		</AlertBar>
	);
}
